import CreditIssuanceTableWrapper from "@/components/registry/CreditIssuanceTableWrapper";
import CreditRetirementTableWrapper from "@/components/registry/CreditRetirementTableWrapper";
import CreditTransferTableWrapper from "@/components/registry/CreditTransferTableWrapper";
import RegistryCsvDownloadButton from "@/components/registry/RegistryCsvDownloadButton";
import RegistryProjectTableWrapper from "@/components/registry/RegistryProjectTabelWrapper";
import SEO from "@/components/shared/SEO";
import LinkWithDivider from "@/components/shared/ui/LinkWithDivider";
import CustomTab from "@/components/shared/ui/Tabs";
import { OCP_MTD_PUBLIC_STATS, OCP_PROJECTS_PUBLIC_STATS } from "@/constants";
import { PublicRegistryCsvExport } from "@/hooks/registry/useDownloadPublicRegistryCsv";
import Image from "next/image";
import { useState } from "react";

type RegistryViewMode = "projects" | "activities";

function registryCsvExportForView(
	viewMode: RegistryViewMode,
	projectsTab: number,
	activitiesTab: number,
): { request: PublicRegistryCsvExport; label: string } {
	if (viewMode === "projects") {
		if (projectsTab === 1) {
			return {
				request: { resource: "projects", projectStatus: "IN_VALIDATION" },
				label: "Download All Projects in Validation",
			};
		}

		return {
			request: { resource: "projects" },
			label: "Download All Registered Projects",
		};
	}

	if (activitiesTab === 1) {
		return {
			request: { resource: "credits", activity: "TRANSFER" },
			label: "Download All Transfers",
		};
	}

	if (activitiesTab === 2) {
		return {
			request: { resource: "credits", activity: "RETIREMENT" },
			label: "Download All Retirements",
		};
	}

	return {
		request: { resource: "credits" },
		label: "Download All Issuances",
	};
}

const RegistryPage = () => {
	const [viewMode, setViewMode] = useState<RegistryViewMode>("projects");
	const [projectsTab, setProjectsTab] = useState(0);
	const [activitiesTab, setActivitiesTab] = useState(0);
	const csvExport = registryCsvExportForView(viewMode, projectsTab, activitiesTab);
	const csvDownload = (
		<div className="flex justify-end pt-8">
			<RegistryCsvDownloadButton exportRequest={csvExport.request} label={csvExport.label} />
		</div>
	);

	return (
		<>
			<SEO title="OCP Registry" />
			<div className="min-h-screen px-0">
				<div className="pt-0">
					<PlatformStatusCover />
					<div className="registry-table-container relative px-10 md:px-16">
						<div className="absolute right-10 top-0 flex h-10 items-center md:right-16">
							<div className="flex rounded-full bg-gray-200 p-[4px]">
								<button
									type="button"
									onClick={() => setViewMode("projects")}
									className={`rounded-full px-4 py-[3px] text-sm font-semibold text-gray-700 transition-colors ${
										viewMode === "projects"
											? "border border-gray-200 bg-white shadow-sm"
											: "bg-transparent hover:text-gray-900"
									}`}
								>
									Projects
								</button>
								<button
									type="button"
									onClick={() => setViewMode("activities")}
									className={`rounded-full px-4 py-[3px] text-sm font-semibold text-gray-700 transition-colors ${
										viewMode === "activities"
											? "border border-gray-200 bg-white shadow-sm"
											: "bg-transparent hover:text-gray-900"
									}`}
								>
									Activity
								</button>
							</div>
						</div>

						{viewMode === "projects" && (
							<CustomTab
								className="max-w-xs"
								labels={["Registered Projects", "Projects in Validation"]}
								noTopMarginOnContent={true}
								tabHeaderOverride="w-full [text-transform:none] !text-[14px]"
								selectedIndex={projectsTab}
								onChange={setProjectsTab}
								panelFooter={csvDownload}
							>
								<RegistryProjectTableWrapper status="VALIDATED" />
								<RegistryProjectTableWrapper status="IN_VALIDATION" />
							</CustomTab>
						)}

						{viewMode === "activities" && (
							<CustomTab
								className="max-w-[390px]"
								labels={["Issuances", "Transfers", "Retirements"]}
								noTopMarginOnContent={true}
								tabHeaderOverride="w-full [text-transform:none] !text-[14px]"
								selectedIndex={activitiesTab}
								onChange={setActivitiesTab}
								panelFooter={csvDownload}
							>
								<CreditIssuanceTableWrapper />
								<CreditTransferTableWrapper />
								<CreditRetirementTableWrapper />
							</CustomTab>
						)}
					</div>
					<PlatformStatus
						viewMode={viewMode}
						projectsTab={projectsTab}
						setViewMode={setViewMode}
						setProjectsTab={setProjectsTab}
					/>
				</div>
			</div>
		</>
	);
};

export default RegistryPage;

export function PlatformStatusCover() {
	return (
		// <aside className="bg-gradient-to-t from-white to-blue-200 py-20 pb-0 pr-8">
		<aside className="bg-transparent py-12 pr-8 lg:py-0 lg:pb-0">
			<div className=" flex items-center justify-between gap-8 px-4">
				<div className="ocp-info-text !mb-0 w-full md:w-[50%]">
					<h1 className="mb-4 text-gray-900">Registry</h1>
					<p className="mb-4 leading-relaxed text-gray-700">
						The OCP Registry combines comprehensive project data and integrated dMRV, rating and insurance information,
						designed around ICVCM's Core Carbon Principles.
					</p>
					<p className="leading-relaxed text-gray-700">
						Here, you can track projects and credit information, including what credits are available to purchase.
					</p>
				</div>
				<div className="hidden w-[50%] justify-center md:flex">
					<Image
						src="/404.png"
						alt="Project Table"
						width={0}
						height={0}
						sizes={"full"}
						style={{
							width: "28vw",
							height: "28vw",
						}}
					/>
				</div>
			</div>
		</aside>
	);
}

export function PlatformStatus({
	viewMode,
	projectsTab,
	setViewMode,
	setProjectsTab,
}: {
	viewMode: RegistryViewMode;
	projectsTab: number;
	setViewMode: (mode: RegistryViewMode) => void;
	setProjectsTab: (tab: number) => void;
}) {
	const goToProjectsTab = (tab: number) => {
		setViewMode("projects");
		setProjectsTab(tab);
		const element = document.querySelector(".registry-table-container");
		if (element) {
			element.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	};

	return (
		<aside className="mt-6 bg-transparent pb-36 pt-4">
			<h2 className="ocp-heading-text mb-6 text-center text-2xl text-gray-900">Platform Status</h2>
			<div className="px-4">
				<LinkWithDivider
					title={`${OCP_PROJECTS_PUBLIC_STATS.activeCount} Registered Project${
						OCP_PROJECTS_PUBLIC_STATS.activeCount > 1 ? "s" : ""
					}`}
					isFirstItemOfGroup={true}
					borderX={true}
					showArrowWithoutHref={viewMode !== "projects" || projectsTab !== 0}
					onClick={() => goToProjectsTab(0)}
				/>
				<LinkWithDivider
					title={`${OCP_PROJECTS_PUBLIC_STATS.inValidationCount} Project${
						OCP_PROJECTS_PUBLIC_STATS.inValidationCount > 1 ? "s" : ""
					} in Validation`}
					borderX={true}
					showArrowWithoutHref={viewMode !== "projects" || projectsTab !== 1}
					onClick={() => goToProjectsTab(1)}
				/>
				<LinkWithDivider
					title={`${OCP_MTD_PUBLIC_STATS.publishedCount} Published ${
						OCP_MTD_PUBLIC_STATS.publishedCount > 1 ? "Methodologies" : "Methodology"
					}`}
					href="/all-methodologies"
					borderX={true}
				/>
				<LinkWithDivider
					title={`${OCP_MTD_PUBLIC_STATS.upcomingCount} Upcoming ${
						OCP_MTD_PUBLIC_STATS.upcomingCount > 1 ? "Methodologies" : "Methodology"
					}`}
					href="/upcoming-methodologies"
					borderX={true}
				/>
			</div>
		</aside>
	);
}
